import { PAYMENT_GATEWAY_LABELS, placeName, type CountryProfile } from './countryProfiles';

export interface CountryHighlight {
  icon: string;
  title: string;
  description: string;
}

export interface CountryPitch {
  eyebrow: string;
  heading: string;
  intro: string;
  highlights: CountryHighlight[];
  /** Shown when Gradeverse integrates no gateway there: ask, don't promise. */
  gatewayAsk: string | null;
  ctaLabel: string;
}

const listOf = (items: string[]) =>
  items.length <= 1 ? items.join('') : `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;

/** Copy for the landing page's country section, built from the visitor's profile. */
export function countryPitch(profile: CountryProfile, isProfiled: boolean): CountryPitch {
  const where = isProfiled ? placeName(profile) : 'your country';
  const gateways = profile.gateways.map((gateway) => PAYMENT_GATEWAY_LABELS[gateway]);
  const period = profile.academicPeriod === 'term' ? 'Terms' : 'Semesters';

  const highlights: CountryHighlight[] = [
    {
      icon: 'pi pi-calendar',
      title: `${period}, sessions and your school structure`,
      description: `Set up ${profile.schoolLevels.toLowerCase()} classes or tertiary programmes with the academic calendar ${where} runs on — terms or semesters.`,
    },
  ];

  if (gateways.length > 0) {
    highlights.push({
      icon: 'pi pi-credit-card',
      title: `Collect fees with ${listOf(gateways)}`,
      description: `Parents and students pay online through gateways that operate in ${where}; payments reconcile against each student's bill automatically.`,
    });
  }

  if (profile.examBodies.length > 0) {
    highlights.push({
      icon: 'pi pi-pencil',
      title: `CBT practice for ${listOf(profile.examBodies)}`,
      description: 'Run computer-based tests, continuous assessment and mock exams, with results flowing straight into report cards.',
    });
  } else {
    highlights.push({
      icon: 'pi pi-pencil',
      title: 'CBT, assessments and report cards',
      description: 'Run computer-based tests, continuous assessment and exams, with results flowing straight into report cards.',
    });
  }

  highlights.push({
    icon: 'pi pi-globe',
    title: 'Cloud-hosted, works on any device',
    description: 'No servers to install. Staff, students and parents sign in from a phone, tablet or laptop wherever they are.',
  });

  const isNigeria = profile.code === 'NG';

  return {
    eyebrow: isProfiled ? `Gradeverse in ${where}` : 'Gradeverse worldwide',
    heading: isNigeria
      ? 'Built with Nigerian schools and universities'
      : `Built for schools in ${where}`,
    intro: isNigeria
      ? 'From WAEC-bound secondary schools to multi-faculty universities, Gradeverse already runs admissions, fees, CBT and results for Nigerian institutions.'
      : `Gradeverse runs admissions, fees, CBT and results for schools and universities. We're onboarding institutions in ${where} now — tell us what you need and we'll tailor the setup.`,
    highlights,
    gatewayAsk: gateways.length === 0
      ? `Using a payment gateway in ${where}? Tell us which one — we prioritise new integrations by demand.`
      : null,
    ctaLabel: isNigeria ? 'Request a demo' : `Be one of the first in ${where}`,
  };
}
